import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type PlanTier = "clarity" | "circle" | "advisory_board";

interface ViewAsContextType {
  viewAsPlan: PlanTier | null;
  effectivePlan: string;
  isViewingAs: boolean;
  isAdmin: boolean;
  setViewAsPlan: (plan: PlanTier | null) => void;
  exitViewAs: () => void;
}

const ViewAsContext = createContext<ViewAsContextType | undefined>(undefined);

const ADMIN_ROLES = ["admin", "super_admin"] as const;
const STORAGE_KEY = "outspoken_view_as_plan";

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [viewAsPlan, setViewAsPlanState] = useState<PlanTier | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored as PlanTier | null;
  });
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin role
  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setIsAdmin(!!data?.some((r) => ADMIN_ROLES.includes(r.role as any)));
      });
  }, [user]);

  // Clear view-as if not admin
  useEffect(() => {
    if (!isAdmin && viewAsPlan) {
      setViewAsPlanState(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isAdmin]);

  const setViewAsPlan = (plan: PlanTier | null) => {
    if (!isAdmin) return;
    setViewAsPlanState(plan);
    if (plan) localStorage.setItem(STORAGE_KEY, plan);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const exitViewAs = () => setViewAsPlan(null);

  // Map profile subscription_tier to a consistent plan string
  const realPlan = profile?.subscription_tier || "free";
  const effectivePlan = isAdmin && viewAsPlan ? viewAsPlan : realPlan;

  return (
    <ViewAsContext.Provider value={{ viewAsPlan, effectivePlan, isViewingAs: isAdmin && !!viewAsPlan, isAdmin, setViewAsPlan, exitViewAs }}>
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  const ctx = useContext(ViewAsContext);
  if (!ctx) throw new Error("useViewAs must be used within ViewAsProvider");
  return ctx;
}
